import {
  Apps as AppsIcon,
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon,
  ExitToApp as ExitToAppIcon,
  Help as HelpIcon,
  LibraryBooks as LibraryBooksIcon,
  People as PeopleIcon,
  Settings as SettingsIcon
} from '@material-ui/icons';
import { flow } from 'lodash';
import { filter, groupBy, values, map } from 'lodash/fp';
import AppBar from 'components/@layout/AppBar';
import NavBar from 'components/@layout/NavBar';
import getAdminModuleRoutes from 'components/AdminConsoleModule/routes';
import getAppAccessRoutes from 'components/AppAccessModule/routes';
import getClassroomsModuleRoutes from 'components/ClassroomsModule/routes';
import getDashboardRoutes from 'components/DashboardModule/routes';
import getResourcesModuleRoutes from 'components/ResourcesModule/routes';
import getSupportRoutes from 'components/SupportModule/routes';
import AdminModule from 'components/AdminConsoleModule';
import AppAccessModule from 'components/AppAccessModule';
import ClassroomsModule from 'components/ClassroomsModule';
import Dashboard from 'components/DashboardModule';
import LogoutFrame from 'components/LogoutFrame';
import OrdersModule from 'components/Orders';
import Reports from 'components/ReportsModule';
import ResourcesModule from 'components/ResourcesModule';
import SupportModule from 'components/SupportModule';
import UserProfile from 'components/UserProfile';
import Config from 'config';
import { PrivilegesCache } from 'store/user';
import { RouteId, RouteConfig } from 'types';

const { privilegesGroups } = Config;

/** This is used for rendering app-level routes **/
export const getAppRootRoutes = (): RouteConfig[] =>
  routes.filter(x => !Config.hiddenRoutes.find(y => y === x.id));

/** This is used for matching menu item selection **/
export const getAllAppRoutes = (): RouteConfig[] => [
  ...getAppRootRoutes(),
  ...getResourcesModuleRoutes(),
  ...getClassroomsModuleRoutes(),
  ...getAdminModuleRoutes(),
  ...getAppAccessRoutes(),
  ...getSupportRoutes(),
  ...getAdminModuleRoutes(),
  ...getDashboardRoutes()
];

export const getUnauthenticatedRoutes = (): RouteConfig[] => unauthenticatedRoutes;

/** This is used to render menu items  */
export const getMenuItemsGrouped = (userPrivilegesCache: PrivilegesCache): RouteConfig[][] => {
  return flow(
    filter((x: RouteConfig) => x.MenuIcon && userPrivilegesCache.isPrivileged(x.privileges ?? [])),
    groupBy('menuGroup'),
    values,
    map((x: RouteConfig[]) => [
      ...x.sort((a, b) =>
        a.menuSortIndex !== undefined && b.menuSortIndex !== undefined
          ? a.menuSortIndex - b.menuSortIndex
          : -1
      )
    ])
  )(getAllAppRoutes());
};

/** This is used when computing selected menu item  */
export const getMenuItemsUngrouped = (): RouteConfig[] => {
  return getAllAppRoutes().filter((x: RouteConfig) => x.MenuIcon);
};

const routes: RouteConfig[] = [
  {
    /* included in menu by default (custom component) for now */
    Component: UserProfile,
    NavBar,
    AppBar,
    path: '/profile',
    getTitle: () => 'Profile',
    getBreadcrumbs: () => [{ name: 'Profile', path: '/profile', Icon: PeopleIcon }],
    id: RouteId.USER_PROFILE,
    privileges: []
  },
  {
    Component: Dashboard,
    MenuIcon: DashboardIcon,
    menuGroup: 1,
    menuSortIndex: 0,
    path: '/dashboard',
    getTitle: () => 'Dashboard',
    getBreadcrumbs: () => [{ name: 'Dashboard', path: '/dashboard', Icon: DashboardIcon }],
    id: RouteId.DASHBOARD,
    privileges: []
  },
  {
    Component: Reports,
    MenuIcon: AssessmentIcon,
    menuGroup: 1,
    menuSortIndex: 1,
    path: '/reports',
    getTitle: () => 'Reports',
    getBreadcrumbs: () => [{ name: 'Reports', path: '/reports', Icon: AssessmentIcon }],
    id: RouteId.REPORTS,
    privileges: []
  },
  {
    Component: ClassroomsModule,
    menuGroup: 1,
    menuSortIndex: 2,
    MenuIcon: PeopleIcon,
    path: '/classrooms',
    getTitle: () => 'My Classes & Students',
    getBreadcrumbs: () => [
      { name: 'My Classes & Students', path: '/classrooms', Icon: PeopleIcon }
    ],
    id: RouteId.CLASSROOMS,
    privileges: []
  },
  {
    Component: ResourcesModule,
    MenuIcon: LibraryBooksIcon,
    menuGroup: 1,
    menuSortIndex: 3,
    path: '/resources',
    getTitle: () => 'Teaching Resources',
    getBreadcrumbs: () => [
      { name: 'Teaching Resources', path: '/resources', Icon: LibraryBooksIcon }
    ],
    id: RouteId.RESOURCES,
    privileges: []
  },
  {
    Component: AdminModule,
    MenuIcon: SettingsIcon,
    menuGroup: 1,
    menuSortIndex: 4,
    path: '/admin',
    getTitle: () => 'Admin Console',
    getBreadcrumbs: () => [{ name: 'Admin Console', path: '/admin', Icon: SettingsIcon }],
    id: RouteId.ADMIN_CONSOLE,
    privileges: privilegesGroups.moduleLevel.adminConsole.any
  },
  {
    Component: AppAccessModule,
    MenuIcon: AppsIcon,
    menuGroup: 2,
    menuSortIndex: 0,
    path: '/app-access',
    getTitle: () => 'Application Access',
    getBreadcrumbs: () => [{ name: 'Application Access', path: '/app-access', Icon: AppsIcon }],
    id: RouteId.APP_ACCESS,
    privileges: []
  },
  {
    Component: SupportModule,
    MenuIcon: HelpIcon,
    menuGroup: 2,
    menuSortIndex: 1,
    path: '/support',
    getTitle: () => 'Help & Support',
    getBreadcrumbs: () => [{ name: 'Help & Support', path: '/support', Icon: HelpIcon }],
    id: RouteId.SUPPORT,
    privileges: []
  },
  {
    path: '/orders',
    Component: OrdersModule,
    getTitle: () => 'Orders',
    id: RouteId.ORDERS,
    privileges: []
  },
  {
    Component: LogoutFrame,
    NavBar,
    AppBar,
    MenuIcon: ExitToAppIcon,
    menuGroup: 3,
    menuSortIndex: 2,
    path: '/logout',
    getTitle: () => 'Logout',
    getBreadcrumbs: () => [{ name: 'Logout', path: '/logout', Icon: ExitToAppIcon }],
    id: RouteId.LOGOUT,
    privileges: []
  }
];

const unauthenticatedRoutes: RouteConfig[] = [];
